import React from 'react';
import { Checkbox, makeStyles, tokens, Tooltip } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import dynTrashCanIcon from '../../assets/bot-icons/dyn-icon-trash-can.svg';
import { Icon } from '../utils/Icon';

import { ChatHistoryDto } from '../../api/models';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    setSelectedConversation,
    setConversations,
    removeConversation,
} from '../../redux/features/conversations/conversationsSlice';
import { ChatService } from '../../libs/services/ChatService';
import { CustomerLiveChatService } from '../../libs/services/CustomerLiveChatService';
import { BackendServiceUrl } from '../../libs/services/BaseService';
import { Constants } from '../../Constants';
import { SharedStyles } from '../../styles';

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalM,
        height: '100%',
        ...SharedStyles.scroll,
        paddingRight: '16px',
        paddingTop: '5px',
    },
    group: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        marginBottom: tokens.spacingVerticalL,
        paddingRight: '0',
    },
    groupTitle: {
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    item: {
        padding: tokens.spacingVerticalS,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            borderRadius: tokens.borderRadiusMedium,
        },
        display: 'flex',
        alignItems: 'center',
        minHeight: '32px',
        gap: tokens.spacingHorizontalXS,
        paddingLeft: tokens.spacingHorizontalL,
        paddingRight: '0',
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: '0',
    },
    title: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: tokens.colorNeutralForeground2,
        padding: tokens.spacingVerticalL,
        gap: tokens.spacingVerticalL,
        textAlign: 'center',
    },
    emptyIcon: {
        width: '64px',
        height: '64px',
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    emptyTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    emptyText: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        maxWidth: '400px',
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: tokens.colorNeutralForeground2,
        padding: tokens.spacingVerticalL,
        fontSize: tokens.fontSizeBase300,
    },
    deleteIcon: {
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            color: tokens.colorNeutralForeground1,
        },
        width: '16px',
        height: '18px',
    },
    selectionToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
        paddingLeft: tokens.spacingHorizontalL,
        paddingBottom: tokens.spacingVerticalXS,
    },
    selectedCount: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground2,
    },
    itemCheckbox: {
        flexShrink: 0,
    },
});

interface GroupedChats {
    last7Days: ChatHistoryDto[];
    last30Days: ChatHistoryDto[];
    older: ChatHistoryDto[];
}

interface ChatHistoryListProps {
    searchQuery: string;
    // History is owned by HistoryPage (a single useChatHistory fetch) and passed in, so this list and
    // the export button share one result instead of each firing its own request.
    history: ChatHistoryDto[];
    isLoading: boolean;
    historyError: string | null;
    refetch: () => Promise<void>;
    // Selection UI renders only when the export feature is on; the state lives in HistoryPage so the
    // export button can read the chosen ids.
    selectable: boolean;
    selectedIds: Set<string>;
    setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    // Called when "select all" is capped to the export limit (with a message) or cleared (null). The
    // parent renders the notice alongside the export messaging. Optional: the read-only /history
    // wrapper (selectable=false) never triggers it.
    onSelectionNotice?: (message: string | null) => void;
}

const groupChatsByDate = (chats: ChatHistoryDto[]): GroupedChats => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return chats.reduce<GroupedChats>(
        (groups, chat) => {
            const chatDate = chat.lastMessage ? new Date(chat.lastMessage) : new Date();

            if (chatDate >= sevenDaysAgo) {
                groups.last7Days.push(chat);
            } else if (chatDate >= thirtyDaysAgo) {
                groups.last30Days.push(chat);
            } else {
                groups.older.push(chat);
            }

            return groups;
        },
        { last7Days: [], last30Days: [], older: [] },
    );
};

export const ChatHistoryList: React.FC<ChatHistoryListProps> = ({
    searchQuery,
    history,
    isLoading,
    historyError,
    refetch,
    selectable,
    selectedIds,
    setSelectedIds,
    onSelectionNotice,
}) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token ?? '';
    const chatService = new ChatService(token, BackendServiceUrl);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const selectedConversationId = useAppSelector((state: RootState) => state.conversations.selectedId);

    const formatDate = (date: Date | string) => {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        // Use en-GB for English to get British date format
        const locale = i18n.language === 'en' ? 'en-GB' : i18n.language;
        return dateObj.toLocaleString(locale, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };

    const loggedInUser = {
        id: activeUserInfo?.id ?? '',
        fullName: activeUserInfo?.username ?? '',
        emailAddress: activeUserInfo?.email ?? '',
        photo: undefined,
        online: true,
    };

    if (isLoading) {
        return <div className={classes.loading}>{t('app.loadingChats')}</div>;
    }

    if (historyError) {
        return <div className={classes.empty}>Error loading history: {historyError}</div>;
    }

    if (history.length === 0) {
        return (
            <div className={classes.empty}>
                <div className={classes.emptyIcon}>
                    <Icon name="history" size={64} />
                </div>
                <div>
                    <div className={classes.emptyTitle}>{t('chatHistory.noHistoryYet')}</div>
                    <div className={classes.emptyText}>{t('chatHistory.createFirstChat')}</div>
                </div>
            </div>
        );
    }

    // Filter chats based on search query
    const filteredHistory = searchQuery
        ? history.filter((chat) => chat.title?.toLowerCase().includes(searchQuery.toLowerCase()))
        : history;

    const groupedChats = groupChatsByDate(filteredHistory);

    // Select-all operates over the currently visible (search-filtered) chats only; selection state is
    // by id, so it survives search changes and only ever holds ids the user actually chose.
    const visibleIds = filteredHistory.map((chat) => chat.id);
    const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
    const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

    const toggleOne = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAllVisible = (checked: boolean) => {
        if (!checked) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const id of visibleIds) {
                    next.delete(id);
                }
                return next;
            });
            onSelectionNotice?.(null);
            return;
        }

        const limit = Constants.MAX_CHATS_PER_EXPORT;
        if (visibleIds.length > limit) {
            // More visible chats than a single export allows. visibleIds is most-recent-first (history
            // is sorted lastMessage DESC), so keep the first `limit` and tell the user only those were
            // selected — instead of letting the export fail server-side with a 400. Replace semantics:
            // the selection becomes exactly the capped set.
            setSelectedIds(new Set(visibleIds.slice(0, limit)));
            onSelectionNotice?.(t('chatHistory.selectAllTruncated', { limit }));
            return;
        }

        setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const id of visibleIds) {
                next.add(id);
            }
            return next;
        });
        onSelectionNotice?.(null);
    };

    const handleChatClick = async (chatId: string) => {
        try {
            // Load chat messages for the selected chat
            const chatMessages = await chatService.getChatMessagesAsync(chatId, 0, 100, hostContext);

            // Create a new conversation object with the loaded messages
            const loadedConversation = {
                id: chatId,
                users: [loggedInUser],
                messages: chatMessages,
                enabledHostedPlugins: [], // This will be updated when the chat is fully loaded
                botProfilePicture: Constants.bot.profile.photo,
                input: '',
                botResponseStatus: undefined,
                userDataLoaded: false,
                disabled: false,
                copiedText: '',
                botResponseStatusHistory: [],
                hostContext: undefined,
            };

            // Update the conversations state
            dispatch(setConversations({ [chatId]: loadedConversation }));

            // Set the selected conversation
            dispatch(setSelectedConversation(chatId));
            sessionStorage.removeItem('shouldCreateNewChat');

            // Navigate to the chat view
            await navigate('/');
        } catch (error: unknown) {
            console.error('Failed to load chat messages:', error);
        }
    };

    const handleDelete = async (e: React.MouseEvent, chatId: string) => {
        e.stopPropagation(); // Prevent chat selection when clicking delete
        try {
            // If a support agent is handling this chat, deleting it would otherwise strand them with a
            // dead claimed chat: delete only soft-deletes the session, never the LiveChat mode doc, and
            // Human mode has no timeout sweep. Best-effort close it as "user closed chat" FIRST — while
            // the session still exists so the ownership check passes (a close after the soft-delete
            // 404s). Deletion is already an explicit action, so no warning dialog. The backend no-ops
            // for a non-agent-handled chat (#1937). Gated only on `token`, deliberately NOT on
            // LiveChatEnabled: the close endpoint is by design not flag-gated (a chat can still be
            // Human/Queued when the master switch is flipped off mid-rollout, and the customer must
            // still be able to abandon it) — mirroring useRequestNewChat's handover-close branch.
            if (token) {
                try {
                    await new CustomerLiveChatService(token).closeAsync(chatId, hostContext);
                } catch {
                    // best-effort — proceed with the delete regardless.
                }
            }
            await chatService.deleteChatSessionAsync(chatId);
            dispatch(removeConversation(chatId));
            if (chatId === selectedConversationId) {
                sessionStorage.setItem('shouldCreateNewChat', 'true');
            }
            // Drop the deleted chat from the export selection so a gone chat can't stay selected.
            setSelectedIds((prev) => {
                if (!prev.has(chatId)) {
                    return prev;
                }
                const next = new Set(prev);
                next.delete(chatId);
                return next;
            });
            await refetch(); // Refetch the chat history to update the list
        } catch (error) {
            console.error('Failed to delete chat:', error);
        }
    };

    const renderGroup = (chats: ChatHistoryDto[], title: string) => {
        if (chats.length === 0) return null;

        return (
            <div className={classes.group}>
                <div className={classes.groupTitle}>{title}</div>
                {chats.map((item) => (
                    <div
                        key={item.id}
                        className={classes.item}
                        onClick={() => {
                            void handleChatClick(item.id);
                        }}
                        data-testid={`chat-history-list-item-${item.id}`}
                    >
                        {selectable && (
                            <Checkbox
                                className={classes.itemCheckbox}
                                checked={selectedIds.has(item.id)}
                                onChange={() => {
                                    toggleOne(item.id);
                                }}
                                onClick={(e) => {
                                    // Toggling the checkbox must not also open the chat.
                                    e.stopPropagation();
                                }}
                                aria-label={t('chatHistory.selectChatAria', {
                                    title: item.title ?? t('chatHistory.noTitle'),
                                })}
                            />
                        )}
                        <div className={classes.titleContainer}>
                            <Tooltip
                                content={item.lastMessage ? formatDate(item.lastMessage) : ''}
                                relationship="label"
                                positioning="above"
                            >
                                <div className={classes.title}>{item.title ?? t('chatHistory.noTitle')}</div>
                            </Tooltip>
                        </div>
                        <img
                            src={dynTrashCanIcon}
                            alt="Delete"
                            className={classes.deleteIcon}
                            onClick={(e) => {
                                void handleDelete(e, item.id);
                            }}
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={classes.list}>
            {selectable && (
                <div className={classes.selectionToolbar}>
                    <Checkbox
                        checked={allVisibleSelected ? true : someVisibleSelected ? 'mixed' : false}
                        onChange={(_, data) => {
                            toggleAllVisible(data.checked === true);
                        }}
                        label={t('chatHistory.selectAll')}
                    />
                    {selectedIds.size > 0 && (
                        <span className={classes.selectedCount}>
                            {t('chatHistory.selectedCount', { count: selectedIds.size })}
                        </span>
                    )}
                </div>
            )}
            {renderGroup(groupedChats.last7Days, t('chatHistory.last7Days'))}
            {renderGroup(groupedChats.last30Days, t('chatHistory.last30Days'))}
            {renderGroup(groupedChats.older, t('chatHistory.older'))}
        </div>
    );
};
